/**
 * fsConstraints.js - FS date-constraint engine: a target may NEVER start at or
 * before its source's end. targetStart >= sourceEnd + 1 (inclusive dates).
 *
 * This file exists in TWO byte-identical copies:
 *   lambda/layer/nodejs/fsConstraints.js   (backend, via /opt/nodejs/fsConstraints)
 *   frontend/src/utils/fsConstraints.js    (frontend)
 * A jest test asserts the copies are identical - edit BOTH together.
 *
 * The engine works in integer DAY NUMBERS so calendar projects and
 * relative-day templates share one implementation: toDayNum/fromDayNum convert
 * ISO 'YYYY-MM-DD' strings; template callers pass their day integers straight
 * through.
 *
 * Rules (Bill, 2026-07-29):
 * - FS is a MINIMUM: gaps between source end and target start are legal and
 *   preserved. Nothing ever pulls tasks together.
 * - MOVE drags and TYPED dates CLAMP at the boundary - nothing else moves.
 * - RESIZE pushes: all links, full chain, cycle-guarded. Per-hop semantics
 *   match the established single-hop push: extending a source shrinks its
 *   target from the left (start = sourceEnd + 1, end unchanged); dragging a
 *   target's start left shrinks its source from the right (end =
 *   targetStart - 1, start unchanged). When a push would invert a bar it
 *   collapses to one day at the boundary and the overflow cascades onward.
 *
 * Context shape (built by callers):
 *   ctx = {
 *     tasksByKey: { [key]: { start, end } },   // day numbers, inclusive
 *     edges: [ { source, target } ]            // keys into tasksByKey
 *   }
 * Keys are caller-chosen (e.g. `${numericRef}|${projectId}`); the engine never
 * parses dependsOn - callers build edges from the canonical link contract in
 * dependencyRefs.js.
 */

const MS_PER_DAY = 86400000;

function toDayNum(value) {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    const parsed = Date.parse(String(value).slice(0, 10) + 'T00:00:00Z');
    if (Number.isNaN(parsed)) return null;
    return Math.round(parsed / MS_PER_DAY);
}

function fromDayNum(dayNum) {
    return new Date(dayNum * MS_PER_DAY).toISOString().slice(0, 10);
}

function sourcesOf(ctx, key) {
    return ctx.edges.filter(e => e.target === key).map(e => e.source);
}

function targetsOf(ctx, key) {
    return ctx.edges.filter(e => e.source === key).map(e => e.target);
}

/**
 * Every violated pair in the current data:
 * [{ source, target, sourceEnd, targetStart }]
 */
function findViolations(ctx) {
    const violations = [];
    for (const edge of ctx.edges) {
        const src = ctx.tasksByKey[edge.source];
        const tgt = ctx.tasksByKey[edge.target];
        if (!src || !tgt) continue;
        if (src.end == null || tgt.start == null) continue;
        if (tgt.start <= src.end) {
            violations.push({
                source: edge.source,
                target: edge.target,
                sourceEnd: src.end,
                targetStart: tgt.start
            });
        }
    }
    return violations;
}

/**
 * Legal start-range for a whole-bar MOVE of `key` with duration preserved.
 * Lower bound from sources (must start after every source end); upper bound
 * from targets (must end before every target start).
 */
function moveBounds(ctx, key, duration) {
    let minStart = -Infinity;
    let maxEnd = Infinity;
    for (const s of sourcesOf(ctx, key)) {
        const src = ctx.tasksByKey[s];
        if (src && src.end != null) minStart = Math.max(minStart, src.end + 1);
    }
    for (const t of targetsOf(ctx, key)) {
        const tgt = ctx.tasksByKey[t];
        if (tgt && tgt.start != null) maxEnd = Math.min(maxEnd, tgt.start - 1);
    }
    return { minStart, maxStart: maxEnd - duration };
}

/**
 * CLAMP a whole-bar move. Duration preserved; the bar stops at whichever
 * dependency boundary it hits first. If the constraints leave no legal
 * position (pre-existing bad data), the original dates are returned.
 */
function clampMove(ctx, key, newStart, newEnd) {
    const original = ctx.tasksByKey[key];
    const duration = newEnd - newStart;
    const { minStart, maxStart } = moveBounds(ctx, key, duration);
    if (minStart > maxStart) {
        return { start: original.start, end: original.end };
    }
    const start = Math.min(Math.max(newStart, minStart), maxStart);
    return { start, end: start + duration };
}

/**
 * CLAMP a typed/selected START date (end unchanged).
 * Must sit after every source end, and not after its own end.
 */
function clampStart(ctx, key, newStart) {
    const task = ctx.tasksByKey[key];
    let lo = -Infinity;
    for (const s of sourcesOf(ctx, key)) {
        const src = ctx.tasksByKey[s];
        if (src && src.end != null) lo = Math.max(lo, src.end + 1);
    }
    return Math.min(Math.max(newStart, lo), task.end);
}

/**
 * CLAMP a typed/selected END date (start unchanged).
 * Must sit before every target start, and not before its own start.
 */
function clampEnd(ctx, key, newEnd) {
    const task = ctx.tasksByKey[key];
    let hi = Infinity;
    for (const t of targetsOf(ctx, key)) {
        const tgt = ctx.tasksByKey[t];
        if (tgt && tgt.start != null) hi = Math.min(hi, tgt.start - 1);
    }
    return Math.max(Math.min(newEnd, hi), task.start);
}

/**
 * RESIZE with full-chain push. Returns every date change needed to keep the
 * whole graph valid, the resized task included:
 *   { changes: { [key]: { start, end } } }
 * Gaps are preserved: a link is only touched when actually violated, and each
 * push moves the minimum. Cycle-guarded by an iteration cap (dependency data
 * is a DAG; the cap is a backstop, not an expected path).
 */
function cascadeResize(ctx, key, newStart, newEnd) {
    const changes = {};
    changes[key] = { start: newStart, end: newEnd };
    // Discovery order, primary first. Callers COMMIT IN REVERSE (deepest
    // pushed task first, primary last) so every intermediate write is valid
    // against the previous one - required for the server-side 409 backstop.
    const order = [key];
    const queue = [key];
    const cap = Math.max(64, ctx.edges.length * 4);
    let iterations = 0;

    const current = (k) => changes[k] || ctx.tasksByKey[k];

    while (queue.length > 0) {
        if (++iterations > cap) break; // cycle backstop - never loops forever
        const k = queue.shift();
        const cur = current(k);
        if (!cur) continue;

        // Forward: my end may have pushed into my targets
        for (const t of targetsOf(ctx, k)) {
            const tgt = current(t);
            if (!tgt || tgt.start == null) continue;
            if (tgt.start <= cur.end) {
                const start = cur.end + 1;
                const end = Math.max(tgt.end, start); // inversion collapses to 1 day, overflow cascades
                if (start !== tgt.start || end !== tgt.end) {
                    if (!changes[t]) order.push(t);
                    changes[t] = { start, end };
                    queue.push(t);
                }
            }
        }

        // Backward: my start may have pushed into my sources
        for (const s of sourcesOf(ctx, k)) {
            const src = current(s);
            if (!src || src.end == null) continue;
            if (src.end >= cur.start) {
                const end = cur.start - 1;
                const start = Math.min(src.start, end); // inversion collapses to 1 day, overflow cascades
                if (end !== src.end || start !== src.start) {
                    if (!changes[s]) order.push(s);
                    changes[s] = { start, end };
                    queue.push(s);
                }
            }
        }
    }

    return { changes, order };
}

module.exports = {
    toDayNum,
    fromDayNum,
    findViolations,
    clampMove,
    clampStart,
    clampEnd,
    cascadeResize
};
